import { Immutable } from "immer";
import React from "react";

import type { ProductFeaturesCalloutBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { getSpacingClasses } from "../_spacing/spacing";

type IProps = Immutable<{
    value: Value;
}>;

/**
 * Width-descriptor srcset with no sizes/width/height: the browser
 * density-corrects intrinsic sizing so the image fills its `max-width` box
 * (see the block SCSS), reproducing the legacy lazysizes `data-sizes="auto"`.
 * width/height would pin layout to the raw pixel width (the regression).
 */
const renderProductImage = (
    image: Immutable<NonNullable<Value["product_image"]>>,
): React.JSX.Element => {
    const rendition = image.renditions["width-1000"];
    const url = rendition?.url ?? image.url;
    const width = rendition?.width ?? image.width;
    return (
        <img
            src={url}
            srcSet={`${url} ${width}w`}
            loading="lazy"
            alt={image.title}
        />
    );
};

export const ProductFeaturesCalloutBlock = (
    props: IProps,
): React.JSX.Element | null => {
    const v = props.value;
    const features = v.product_features ?? [];
    return (
        <section
            className={concatClassNames([
                "product-features-callout-block",
                v.background_style?.style ?? "",
                v.spacing ? getSpacingClasses(v.spacing) : "",
            ])}
        >
            {v.header_content && (
                <header>
                    <RichTextBlock value={v.header_content} />
                </header>
            )}
            <div>
                <ul>
                    {/* Index key: ListBlock children don't surface a stable
                        id, so CMS reorders can reuse the wrong feature DOM.
                        Proper fix requires exposing an id from the Python
                        block (via get_api_representation). */}
                    {features.map((feature, i) => (
                        <li key={i}>
                            {feature.icon && (
                                <img
                                    src={
                                        feature.icon.renditions["width-100"]
                                            ?.url ?? feature.icon.url
                                    }
                                    alt={feature.icon.title}
                                />
                            )}
                            <RichTextBlock value={feature.content ?? ""} />
                        </li>
                    ))}
                </ul>
                {v.product_image && renderProductImage(v.product_image)}
            </div>
        </section>
    );
};
